import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { ProjectStatus } from '../../shared/models/project-status.enum';
import { ProjectTimelineService } from '../project-timeline.service';

@Injectable({
  providedIn: 'root'
})
export class InvestmentValidationService {

  constructor(
    private router: Router,
    private timelineService: ProjectTimelineService
  ) {}

  /**
   * Valida todas las condiciones para poder invertir.
   * Retorna 'true' si se puede proceder a la inversión o 'false' en caso contrario.
   *
   * @param info - Información del proyecto
   * @param eurosSold - Cantidad de euros vendidos hasta el momento
   * @param userInversor - Flag que indica si el usuario tiene autorización para venta privada
   * @param percentageToPrivateSale - Porcentaje máximo permitido en venta privada
   * @returns boolean - true si se puede invertir; false si se detecta alguna restricción.
   */
  validateInvestment(info: any, eurosSold: number, userInversor: boolean, percentageToPrivateSale: number): boolean {
    if (!info) {
      Swal.fire('Error', 'No se ha recibido la información del proyecto.', 'error');
      return false;
    }
    const targetAmount = info.amountToSell * (info.priceToken / 100);
    let percentageSoldNumber = 0;
    if (info.amountToSell && info.priceToken) {
      percentageSoldNumber = (eurosSold * 100) / targetAmount;
    }

    // --- Validaciones basadas en el estado del proyecto ---
    if (info.proyectStatus === ProjectStatus.SOLD) {
      Swal.fire(
        'Proyecto vendido',
        'Este proyecto ya se ha vendido y no está disponible para inversión.',
        'info'
      );
      return false;
    }

    if (info.proyectStatus === ProjectStatus.PAYING_DIVIDENDS) {
      Swal.fire(
        'Repartiendo dividendos',
        'Este proyecto está en fase de reparto de dividendos y no permite nuevas inversiones.',
        'info'
      );
      return false;
    }

    if (info.proyectStatus === ProjectStatus.IN_PROGRESS) {
      Swal.fire(
        'Proyecto en curso',
        'Este proyecto ya está en fase de ejecución y no permite nuevas inversiones.',
        'info'
      );
      return false;
    }

    if (info.proyectStatus !== ProjectStatus.ACTIVE) {
      Swal.fire(
        'Proyecto no activo',
        'El proyecto no está disponible para inversión en este momento.',
        'info'
      );
      return false;
    }

    if (!info?.blockchain) {
      Swal.fire(
        'Blockchain no disponible',
        'Este proyecto no está disponible para inversión en este momento.',
        'info'
      );
      return false;
    }

    if (eurosSold >= targetAmount) {
      Swal.fire(
        'Proyecto financiado',
        'Este proyecto ya ha alcanzado su objetivo de financiación.',
        'info'
      );
      return false;
    }

    if (this.toFloor(info.endOfSale) === 'Tiempo agotado') {
      Swal.fire(
        'Tiempo agotado',
        'El período de inversión para este proyecto ha finalizado.',
        'info'
      );
      return false;
    }

    if (info?.managingReinvestments ?? info?.managingInvestments) {
      Swal.fire(
        ProjectStatus.MANAGING_REINVESTMENTS,
        'Estamos gestionando sus reinversiones. En breve abriremos el proyecto.',
        'info'
      );
      return false;
    }

    if (info?.onlyInversors) {
      if (!userInversor) {
        Swal.fire(
          'Venta privada',
          'Esta oportunidad se encuentra en venta privada. Para invertir necesitas autorización del equipo Domoblock.',
          'info'
        );
        return false;
      }

      if (percentageSoldNumber >= percentageToPrivateSale) {
        Swal.fire(
          'Venta privada finalizada',
          'La etapa de venta privada ha terminado. Espera la apertura de la venta pública.',
          'info'
        );
        return false;
      } else {
        Swal.fire(
          'Venta privada activa',
          'Puedes invertir en venta privada.',
          'success'
        );
        // En este caso se podría proceder de inmediato a la navegación si se desea:
        // this.router.navigate(['/oportunidad'], { queryParams: { id: info.id } });
        // return true;
      }
    }

    // Si pasa todas las validaciones se permite invertir
    return true;
  }

  /**
   * Método auxiliar para calcular el tiempo restante de la venta.
   * Recibe un timestamp y devuelve una cadena con el tiempo restante o 'Tiempo agotado'.
   *
   * @param endOfSale - Timestamp de finalización de la venta
   * @returns string - Tiempo restante o 'Tiempo agotado'
   */
  toFloor(endOfSale: number): string {
    if (endOfSale === undefined || endOfSale === null || endOfSale === 0) {
      return 'No activo';
    }

    const nowTimeStamp: number = Math.floor(Date.now() / 1000);
    const secondsDiff = endOfSale - nowTimeStamp;
    const minutesDiff = secondsDiff / 60;
    const hoursDiff = minutesDiff / 60;
    const daysDiff = hoursDiff / 24;

    if (daysDiff >= 1) {
      return 'Quedan: ' + Math.floor(daysDiff) + ' días';
    }

    if (hoursDiff >= 1) {
      return 'Quedan: ' + Math.floor(hoursDiff) + ' horas';
    }

    if (minutesDiff >= 1) {
      return 'Quedan: ' + Math.floor(minutesDiff) + ' minutos';
    }

    if (secondsDiff >= 1) {
      return 'Quedan: ' + Math.floor(secondsDiff) + ' segundos';
    }

    return 'Tiempo agotado';
  }

  /**
   * Calcula el estado actual del proyecto basándose en la información y los euros vendidos.
   * Retorna un array de estados.
   *
   * @param info - Información del proyecto
   * @param eurosSold - Cantidad de euros vendidos hasta el momento
   * @returns string[] - Estados actuales del proyecto
   */
  getCurrentStatus(info: any, eurosSold: number): string[] {
    if (!info) {
      return [ProjectStatus.UNKNOWN];
    }

    const targetAmount = info.amountToSell * (info.priceToken / 100);
    let percentageSoldNumber = 0;
    if (info.amountToSell && info.priceToken) {
      const result = (eurosSold * 100) / targetAmount;
      percentageSoldNumber = result;
    }

    // 1. Primero verificar si está vendido
    if (info.proyectStatus === ProjectStatus.SOLD) {
      return [ProjectStatus.SOLD];
    }

    // 2. Verificar si está repartiendo dividendos
    if (info.proyectStatus === ProjectStatus.PAYING_DIVIDENDS) {
      return [ProjectStatus.PAYING_DIVIDENDS];
    }

    // 3. Verificar si está en curso (después de financiado)
    // Nota: Este método ahora es async, pero getCurrentStatus debe ser sync
    // Por ahora, verificamos solo el estado directo del proyecto
    if (percentageSoldNumber >= 100 && info.proyectStatus === ProjectStatus.IN_PROGRESS) {
      return [ProjectStatus.IN_PROGRESS];
    }

    // 4. Verificar si está financiado (solo si no está vendido ni en curso)
    if (percentageSoldNumber >= 100 && info.proyectStatus !== ProjectStatus.SOLD) {
      return [ProjectStatus.FUNDED];
    }

    // 4. Verificar si está gestionando inversiones
    if (this.isManagingInvestments(info, eurosSold, targetAmount)) {
      return [ProjectStatus.ACTIVE, ProjectStatus.MANAGING_REINVESTMENTS];
    }

    // 5. Verificar estados directos
    if (info.proyectStatus === ProjectStatus.UNDER_ANALYSIS) {
      return [ProjectStatus.UNDER_ANALYSIS];
    }

    // 6. Manejar estado Activo
    if (info.proyectStatus === ProjectStatus.ACTIVE) {
      if (
        !info.onlyInversors &&
        this.toFloor(info.endOfSale) !== 'Tiempo agotado' &&
        eurosSold < targetAmount
      ) {
        return [ProjectStatus.ACTIVE];
      }

      if (info.managingReinvestments ?? info.managingInvestments) {
        return [ProjectStatus.MANAGING_REINVESTMENTS];
      }

      return [ProjectStatus.ACTIVE];
    }

    return [ProjectStatus.UNKNOWN];
  }

  /**
   * Verifica si el proyecto está en estado de "Gestionando inversiones".
   *
   * @param info - Información del proyecto
   * @param eurosSold - Cantidad de euros vendidos hasta el momento
   * @param targetAmount - Monto objetivo calculado para la financiación
   * @returns boolean - true si está gestionando inversiones; false de lo contrario.
   */
  private isManagingInvestments(info: any, eurosSold: number, targetAmount: number): boolean {
    const managing = info?.managingReinvestments ?? info?.managingInvestments;
    return (
      info?.proyectStatus === ProjectStatus.ACTIVE &&
      managing &&
      eurosSold < targetAmount
    );
  }

  /**
   * Determina si un proyecto financiado debe mostrarse como "En curso".
   * Un proyecto pasa a "En curso" 2 semanas después de alcanzar 100% de financiación.
   * @param info - Información del proyecto
   * @returns Un booleano que indica si debe mostrarse como "En curso"
   */
  private async shouldShowInProgress(info: any): Promise<boolean> {
    if (!info?.id) return false;
    
    // Obtener fecha de financiación
    const fundedDate = await this.timelineService.getFundedDate(info.id);
    if (!fundedDate) return false;
    
    // Verificar si han pasado 2 semanas (14 días)
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    return fundedDate <= twoWeeksAgo;
  }

  /**
   * Obtiene la fecha aproximada cuando el proyecto se financió.
   * @param info - Información del proyecto
   * @returns Fecha de financiación o null si no se puede determinar
   */
  private async getFundedDate(info: any): Promise<Date | null> {
    if (!info?.id) return null;
    return await this.timelineService.getFundedDate(info.id);
  }
}
